import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  addAdultToGroup, addStudentToGroup,
  removeAdultFromGroup, removeStudentFromGroup
} from "../api/groups";
import { getStudentsByIds, getAdultsByIds } from "../api/people";
import { getCampGroup } from "../api/groups";
import { createStudent, createStay, listStaysForStudent, createAdult, createGroupLeader, listNsrsForAdult, updateNsr, createNsr, bulkCreateStudents, bulkCreateAdults } from "../api/people";


export function useMembers({ groupId, token, initialStudentIds = [], initialAdultIds = [], initialTab = "details" }) {
  const [tab, setTab] = useState(initialTab);
  const [studentIds, setStudentIds] = useState(initialStudentIds);
  const [adultIds, setAdultIds] = useState(initialAdultIds);
  const [studentDetails, setStudentDetails] = useState([]);
  const [adultDetails, setAdultDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  const [stayAlerts, setStayAlerts] = useState([]); 
  const clearStayAlerts = () => setStayAlerts([]);

  const toSql = (d) => {
    if (!d) return null;
    if (typeof d === "string") return d.slice(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const overlaps = (aStart, aEnd, bStart, bEnd) => (aStart <= bEnd && bStart <= aEnd);

  const sameWindow = (aStart, aEnd, bStart, bEnd) =>
    aStart === bStart && aEnd === bEnd;

  // 🔧 reset state whenever a different group is opened
  useEffect(() => {
    setTab(initialTab);
    setStudentIds(initialStudentIds);
    setAdultIds(initialAdultIds);
    setStudentDetails([]);
    setAdultDetails([]);
  }, [groupId, initialStudentIds, initialAdultIds, initialTab]);

  const refresh = useCallback(async (nextS = studentIds, nextA = adultIds) => {
    if (!(tab === "members" || tab === "studentDetails" || tab === "adultDetails")) return;

    setLoading(true);
    try {
      const [sAll, aAll] = await Promise.all([
        getStudentsByIds(nextS, token),
        getAdultsByIds(nextA, token),
      ]);
      setStudentDetails(sAll); setAdultDetails(aAll);
    } finally { setLoading(false); }
  }, [tab, studentIds, adultIds, token]);

  // Re-read membership from the server, then details. For changes made
  // server-side in one go (the student CSV upload) where the ids we hold are
  // stale. Returns the fresh { studentIds, adultIds } so the caller can tell
  // the parent list.
  const reloadMembership = useCallback(async () => {
    const g = await getCampGroup(groupId, token);
    const nextS = Array.isArray(g?.studentIds) ? g.studentIds : studentIds;
    const nextA = Array.isArray(g?.adultIds) ? g.adultIds : adultIds;
    setStudentIds(nextS);
    setAdultIds(nextA);
    await refresh(nextS, nextA);
    return { studentIds: nextS, adultIds: nextA };
  }, [groupId, token, studentIds, adultIds, refresh]);

  const pruneStudents = useCallback((idsToRemove = []) => {
    const kill = new Set(idsToRemove);
    setStudentIds((prev) => prev.filter((id) => !kill.has(id)));
    setStudentDetails((prev) => prev.filter((s) => !kill.has(s?.id)));
  }, []);

  const pruneAdults = useCallback((idsToRemove = []) => {
    const kill = new Set(idsToRemove);
    setAdultIds((prev) => prev.filter((id) => !kill.has(id)));
    setAdultDetails((prev) => prev.filter((a) => !kill.has(a?.id)));
  }, []);


  // lazy-load on tab change or ids change
  useEffect(() => { refresh(); }, [tab, studentIds, adultIds, refresh]);

  /**
  * Ensure the student has a stay for the group's defaults:
  * - exact match => no-op
  * - overlap but not exact => record alert
  * - no overlap => create stay
  */
  const ensureStayForGroup = async ({ studentId, meta }) => {
    const campus = meta?.defaultCampus || null;
    const arrivalDate = toSql(meta?.defaultStartDate);
    const departureDate = toSql(meta?.defaultEndDate);

    if (!campus || !arrivalDate || !departureDate) {
      setStayAlerts(prev => [...prev, {
        studentId,
        type: "invalid-meta",
        message: `Missing group defaults; cannot create stay for ${studentId}.`
      }]);
      return;
    }

    const stays = await listStaysForStudent(token, studentId); // expects [{arrivalDate, departureDate, residential_base/campus, id, ...}]
    const groupStart = arrivalDate;
    const groupEnd   = departureDate;

    // First: exact same campus + window?
    const exact = stays.find(s =>
      (s.residential_base || s.campus) === campus &&
      sameWindow(s.arrivalDate, s.departureDate, groupStart, groupEnd)
    );
    if (exact) {
      // perfect; do nothing
      return;
    }

    // Any overlap with the group window (any campus)
    const overlapping = stays.filter(s =>
      overlaps(s.arrivalDate, s.departureDate, groupStart, groupEnd)
    );

    if (overlapping.length > 0) {
      setStayAlerts(prev => [...prev, {
        studentId,
        type: "overlap",
        message: `Existing stay overlaps group window for ${studentId}.`,
        details: overlapping
      }]);
      // do NOT auto-change; let user resolve
      return;
    }

    // No overlap: create a clean stay matching the group defaults
    await createStay(token, studentId, { campus, arrivalDate, departureDate });
    setStayAlerts(prev => [...prev, {
      studentId,
      type: "created",
      message: `Stay created for ${studentId} (${campus} ${groupStart} → ${groupEnd}).`
    }]);
  };

  const ensureNsrForGroup = async ({ adultId, meta }) => {
  const residentialBase = meta?.defaultCampus || null;
  const arrivaldate = toSql(meta?.defaultStartDate);
  const departuredate = toSql(meta?.defaultEndDate);

  if (!residentialBase || !arrivaldate || !departuredate) {
    setStayAlerts(prev => [...prev, {
      studentId: adultId, // keep same field to avoid refactor, but message clarifies
      type: "invalid-meta",
      message: `Missing group defaults; cannot create adult stay for ${adultId}.`
    }]);
    return;
  }

  const stays = await listNsrsForAdult(token, adultId); // [{id,residentialBase,arrivaldate,departuredate,...}]
  const groupStart = arrivaldate;
  const groupEnd = departuredate;

  const normCampus = (v) => (v ?? "").toString().trim().toLowerCase();
  const d10 = (s) => (s ? s.toString().slice(0, 10) : null);

  const newC = normCampus(residentialBase);
  const newA = d10(groupStart);
  const newD = d10(groupEnd);

  const S = stays.map(st => ({
    id: st.id,
    campus: normCampus(st.residentialBase),
    arr: d10(st.arrivaldate),
    dep: d10(st.departuredate),
  }));

  // already exact?
  const exact = S.find(st => st.campus === newC && st.arr === newA && st.dep === newD);
  if (exact) return;

  // overlap?
  const overlaps = (aStart, aEnd, bStart, bEnd) => (aStart <= bEnd && bStart <= aEnd);
  const overlapping = S.filter(st => overlaps(st.arr, st.dep, newA, newD));

  if (overlapping.length > 0) {
    setStayAlerts(prev => [...prev, {
      studentId: adultId,
      type: "overlap",
      message: `Existing adult stay overlaps group window for ${adultId}.`,
      details: overlapping
    }]);
    return;
  }

  // none: create
  await createNsr(token, adultId, {
    arrivaldate,
    departuredate,
    residentialBase,
    overnightResident: true,
    notes: "",
  });

  setStayAlerts(prev => [...prev, {
    studentId: adultId,
    type: "created",
    message: `Adult stay created for ${adultId} (${residentialBase} ${arrivaldate} → ${departuredate}).`
  }]);
};


  // The membership window is the group's default stay, ending on the last
  // night (departure − 1) as V145/V148 and the feeds resolve it. Without a
  // window the backend stamps start = end = today, which never covers the stay,
  // and the member drops off the arrivals/departures/activity/transport feeds.
  const membershipWindow = (meta) => {
    const startDate = toSql(meta?.defaultStartDate);
    const departure = toSql(meta?.defaultEndDate);
    return {
      startDate,
      endDate: departure ? dayjs(departure).subtract(1, "day").format("YYYY-MM-DD") : null,
    };
  };

  // mutations
  const addStudents = async (ids, meta) => {
    if (!ids?.length) return;
    await Promise.all(ids.map(async (id) => {
      // Stay first, so it exists before the membership that refers to it.
      // A failed stay check is reported, not fatal — the membership is still written.
      if (meta) {
        try {
          await ensureStayForGroup({ studentId: id, meta });
        } catch (err) {
          setStayAlerts(prev => [...prev, { studentId: id, type: "error", message: `Stay check failed for ${id}: ${err?.message || err}` }]);
        }
      }
      await addStudentToGroup(groupId, id, token, membershipWindow(meta));
    }));    
      
    setStudentIds((prev) => {
      const next = Array.from(new Set([...prev, ...ids])); refresh(next, adultIds); return next;
    });
  };

  const addNewStudents = async (count, meta, { onProgress } = {}) => {
  const n = Math.max(0, Math.min(Number(count) || 0, 200));
  if (!n) return;

  const nowSql = new Date().toISOString().slice(0, 10);

  const mkBody = () => ({
    studentName: "",
    studentSurname: "",
    studentDob: null,
    studentGender: "",
    studentNationality: meta?.defaultNationality || "",
    englishLevel: "",
    allergies: "",
    notes: "",
    dateAdded: nowSql,
    hasPoolPermission: false,
    hasPhotoPermission: false,
    hasMedicalPermission: false,
    hasHospitalPermission: false,
    hasExcursionPermission: false,
    hasActivityPermission: false,
    hasSupervisionPermission: false,
    emergencyContact: "",
    mobilePhoneOptOut: false,
    tshirtSize: "",
    mobileNumber: "",
  });

  onProgress?.(`Creating ${n} student${n === 1 ? "" : "s"}…`);

  const students = Array.from({ length: n }, mkBody);

  const campus = meta?.defaultCampus || null;
  const arrivalDate = toSql(meta?.defaultStartDate);
  const departureDate = toSql(meta?.defaultEndDate);

  const stay = (campus && arrivalDate && departureDate)
    ? { campus, arrivalDate, departureDate }
    : undefined;

  const result = await bulkCreateStudents(token, groupId, {
    students,
    stay,
    groupStartDate: toSql(meta?.defaultStartDate),
    groupEndDate: toSql(meta?.defaultEndDate),
  });

  console.log("[bulkCreateStudents] response:", JSON.stringify(result));

  // Handle various possible response shapes from the backend
  const createdIds = result?.ids || result?.studentIds || [];
  if (!createdIds.length) {
    console.warn("[bulkCreateStudents] No IDs returned. Full result:", result);
  }

  setStudentIds((prev) => {
    const next = Array.from(new Set([...prev, ...createdIds]));
    refresh(next, adultIds);
    return next;
  });
};

const addNewAdults = async (count, meta, groupLeaderDefaults, { onProgress } = {}) => {
  const n = Math.max(0, Math.min(Number(count) || 0, 200));
  if (!n) return;

  const nowSql = new Date().toISOString().slice(0, 10);
  const baseName = (groupLeaderDefaults?.groupName || meta?.groupName || "Group").trim();

  const mkAdultBody = () => ({
    dateAdded: nowSql,
    adultName: `${baseName} GL`,
    adultSurname: "",
    adultEmail: "",
    adultPhone: "",
    adultNationality: meta?.defaultNationality || "",
    allergies: "",
    notes: "",
  });

  onProgress?.(`Creating ${n} adult${n === 1 ? "" : "s"}…`);

  const adults = Array.from({ length: n }, mkAdultBody);

  const residentialBase = meta?.defaultCampus || null;
  const arrivalDate = toSql(meta?.defaultStartDate);
  const departureDate = toSql(meta?.defaultEndDate);

  const nsr = (residentialBase && arrivalDate && departureDate)
    ? {
        residentialBase,
        arrivalDate,
        departureDate,
        overnightResident: true,
        notes: "",
      }
    : undefined;

  const result = await bulkCreateAdults(token, groupId, {
    adults,
    nsr,
    groupStartDate: toSql(meta?.defaultStartDate),
    groupEndDate: toSql(meta?.defaultEndDate),
  });

  console.log("[bulkCreateAdults] response:", JSON.stringify(result));

  const createdIds = result?.ids || result?.adultIds || [];
  if (!createdIds.length) {
    console.warn("[bulkCreateAdults] No IDs returned. Full result:", result);
  }

  setAdultIds((prev) => {
    const next = Array.from(new Set([...prev, ...createdIds]));
    refresh(studentIds, next);
    return next;
  });
};


  const removeStudent = async (id) => {
    await removeStudentFromGroup(groupId, id, token);
    setStudentIds((prev) => {
      const next = prev.filter((x) => x !== id); refresh(next, adultIds); return next;
    });
  };
  
  const addAdults = async (ids, meta) => {
  if (!ids?.length) return;

  await Promise.all(ids.map(async (id) => {
    if (meta) {
      try {
        await ensureNsrForGroup({ adultId: id, meta });
      } catch (err) {
        setStayAlerts(prev => [...prev, {
          studentId: id,
          type: "error",
          message: `Adult stay check failed for ${id}: ${err?.message || err}`
        }]);
      }
    }

    await addAdultToGroup(groupId, id, token, membershipWindow(meta));
  }));

  setAdultIds((prev) => {
    const next = Array.from(new Set([...prev, ...ids]));
    refresh(studentIds, next);
    return next;
  });
};

  const removeAdult = async (id) => {
    await removeAdultFromGroup(groupId, id, token);
    setAdultIds((prev) => {
      const next = prev.filter((x) => x !== id); refresh(studentIds, next); return next;
    });
  };

  return {
    // state
    tab, setTab,
    loading,
    studentIds, adultIds,
    studentDetails, adultDetails,
    stayAlerts, clearStayAlerts,
    // actions
    refresh, reloadMembership, addStudents, removeStudent, addAdults, removeAdult, addNewStudents, addNewAdults, pruneStudents, pruneAdults
  };
}
